import type { ColumnDef, Getter } from "@tanstack/react-table";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { useStorageState } from "react-storage-hooks";
import type { SortingRule } from "react-table";
import styled, { useTheme } from "styled-components";
import useSWR from "swr";
import { NumberParam, StringParam, useQueryParams } from "use-query-params";
import { useCookies } from "react-cookie";
import { useInView } from "react-intersection-observer";
import { useAuthContext } from "../../../../components/Auth";
import {
  IconButtonMedium,
  PrimaryButtonLargeV2,
} from "../../../../components/Buttons/Buttons";
import {
  PlusIcon,
  FiltersIconV2,
  ViewListIcon,
  ViewGridIcon,
} from "../../../../components/Icons/Icons";
import { DropDown } from "../../../../components/DropDown/DropDown";
import { FiltersFlyBar } from "../../../../components/Filters/FiltersFlyBar";
import { SelectedFiltersArea } from "../../../../components/Filters/SelectedFiltersArea";
import { Pagination } from "../../../../components/Pagination/Pagination";
import { SearchBar } from "../../../../components/SearchBar/SearchBar";
import {
  getProductStatusColor,
  getProductStatusText,
  StatusLeft,
} from "../../../../components/Status/Status";
import { Table } from "../../../../components/Table/Table";
import { TableWithBulkAction } from "../../../../components/Table/TableWithBulkAction";
import type { TableRefProps } from "../../../../components/Table/utils";
import { WithPermission } from "../../../../components/WithPermission/WithPermission";
import { endpoints } from "../../../../endpoints";
import type {
  TagClassificationConfig,
  WithPagination,
  IPresetDataItem,
  IPresetFilter,
  DataMutate,
  ProductFiltersMainSchema,
} from "../../../../types/types";
import type {
  AttributeSchemaBase,
  ProductStatusType,
  PIMProductMinimalPaginatedOutput,
} from "../../../../types/types.PIM";
import { useRoutePath } from "../../../../util/Routing";
import { useDebounce } from "../../../../util/hooks";
import {
  defaultHandleSort,
  formatDate,
  rowHover,
  updateCustomLabels,
  useMediaQueries,
  useStoreState,
  useSupportedLanguages,
} from "../../../../util/util";
import {
  loadFilters,
  saveFilters,
  getStorageMethod,
} from "../../../../util/filterStateManager";
import { ProductSearchPopup } from "../../../public/ProductDetailsPortfolio/ProductSearchPopup";
import { PRODUCT_SEARCH_POPUP } from "../../../public/pages.public.util";
import { BulkActionOnProducts } from "./BulkActionOnProducts/BulkActionOnProducts";
import type { SellerTableProduct } from "./ProductsList.util";
import { get_all_condensed_products } from "./ProductsList.util";

import ReactTooltip from "react-tooltip";
import type { AxiosError } from "axios";
import axios from "axios";
import { Notifications } from "../../../../components/Notifications/NotificationsContext";
import {
  ContentContainer,
  StickyHeaderContainer,
  FilterButtonContainer,
  SearchAndFiltersRow,
  SearchContainer,
  SelectedFiltersWrapper,
  MainContentWrapper,
  StickyFiltersContainer,
  RightSideControls,
  ViewSwitcher,
  ViewButton,
  ButtonGroup,
  ProductsGridContainer,
  TotalProducts,
  FullwidthPageWrapper,
  StickyFiltersAndTotalWrapper,
  Breadcrumb,
} from "../../../../components/ProductsLayout/SharedProductsLayout";
import { CardProduct } from "../../../../components/CardProduct/CardProduct";
import { DropdownContainer } from "../../../../components/Layout/Layout";
import { TableSearchWrapper } from "../../../admin/SellerAdmin/SellerAdminSettings/PortfolioControlsTab/PorfolioControlsTab";
import { Modal } from "../../../../components/Modal/Modal";
import { SellerCreateQuote } from "../../../Seller/SellerCreateQuote/SellerCreateQuote";
import { SellerCreateSampleRequest } from "../../../Seller/SellerCreateSampleRequest/SellerCreateSampleRequest";
import { Loader } from "../../../../components/Loader/Loader";
import { ErrorPlaceholder } from "../../../../components/Error";
import { useAlternateRequest } from "../../../../hooks/useAlternateRequest";

const SESSION_STORAGE_SELECTED_ROWS_KEY = "selectedRows";

// Specific to ProductsList for its search container customization
const SearchContainerWithMaxWidth = styled(SearchContainer)`
  max-width: 450px;
`;

export type MinimalProductSummary = {
  id: string;
  name: string | null;
  last_modified_full: {
    modified_at: string; // Assuming string based on formatDate usage
  } | null;
  display_status: ProductStatusType | null;
  status: ProductStatusType | null;
  completion_score: number;
  mandatory_completion_score: number | null;
  display_completion_score: number;
  display_mandatory_completion_score: number | null;
  product_number: string;
  chemical_name: string | null;
  show_total_completion_score: boolean;
  show_mandatory_completion_score: boolean;
  pim_product_type: "master" | "variant" | "standard";
};

export type MinimalApiResponse = {
  data: MinimalProductSummary[];
  pagination: {
    total: number;
    offset: number;
    // limit might also be implicitly needed by pagination logic,
    // but not directly accessed in the provided code snippet analysis
  };
};
/**
 * Products list page (e.g. manage > products).  Used for seller admin.
 */
export function ProductsList() {
  const queryObject = () => {
    const querylist: any = {
      product: StringParam,
      q: StringParam,
      offset: NumberParam,
      perPage: NumberParam,
      download: StringParam,
    };
    return querylist;
  };

  const { isLargeScreen } = useMediaQueries();
  const [showFilters, setShowFilters] = useStorageState(
    sessionStorage,
    "productListShowFilters",
    isLargeScreen ? false : true
  );

  const [show_enterprise_search, set_show_enterprise_search] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<IPresetDataItem>();
  const [appliedPresetIds, setAppliedPresetIds] = useState<Set<string>>(
    new Set()
  );

  // Cookie-based view mode persistence
  const [cookies, setCookie] = useCookies(["products-list-view-mode"]);

  const [query, setQuery] = useQueryParams(queryObject());

  const [sortingRules, setSortingRules] = useState<{
    sortBy?: string;
    orderBy: "asc" | "desc";
  }>({
    orderBy: "asc",
  });

  // searchQuery is loaded from the URL if q exists.
  const [searchQuery, setSearchQuery] = useState(query.q || "");
  const [tableHeaders, setTableHeaders] =
    useState<{
      product_name: string;
      product_id: string;
      chemical_name: string;
      last_modified: string;
      completion: string;
      status: string;
    }>();
  const [debouncedSearchQuery] = useDebounce(searchQuery, 1000);
  const { adminPath } = useRoutePath();
  const [offset, setOffset] = useState(query?.offset ?? 0);
  const perPageItems = [10, 20, 50];
  const [perPage, setPerPage] = useState(query.perPage ?? 20);
  const [tableData, setTableData] = useState<SellerTableProduct[]>([]);

  const history = useHistory();
  const location = useLocation();
  const { search, pathname } = location;
  const theme = useTheme();

  const [selectedRows, setSelectedRows] = useState<string[] | "all">(() => {
    // store selectedRows for products page
    const isProductsPage = location.pathname.includes("/pim/products");

    if (isProductsPage) {
      const stored = sessionStorage.getItem(SESSION_STORAGE_SELECTED_ROWS_KEY);
      return stored ? JSON.parse(stored) : [];
    } else {
      sessionStorage.removeItem(SESSION_STORAGE_SELECTED_ROWS_KEY);
      return [];
    }
  });

  useEffect(() => {
    return () => {
      setTimeout(() => {
        if (!window.location.pathname.includes("/pim/products")) {
          sessionStorage.removeItem(SESSION_STORAGE_SELECTED_ROWS_KEY);
        }
      }, 0);
    };
  }, [location.pathname]);

  const handleSelectedRows = (rows: string[] | "all") => {
    sessionStorage.setItem(
      SESSION_STORAGE_SELECTED_ROWS_KEY,
      JSON.stringify(rows)
    );
    setSelectedRows(rows);
  };

  const [tablePagination, setTablePagination] = useState({
    perPage: perPage,
    pageCount: 0,
    pageIndex: 0,
    totalCount: 0,
  });

  // Separate total counts for each view to avoid race conditions
  const [tableTotalCount, setTableTotalCount] = useState(0);
  const [gridTotalCount, setGridTotalCount] = useState(0);

  const [gridPageLimit, setGridPageLimit] = useState(10);

  const [filtersFromUrl, setFiltersFromUrl] =
    useState<{ [key: string]: string[] }>();

  const [appliedFiltersCount, setAppliedFiltersCount] = useState(0);

  // Modal state for quote/sample requests
  const [quoteVariant, setQuoteVariant] = useState<"seller" | null>(null);
  const [sampleVariant, setSampleVariant] = useState<"seller" | null>(null);

  const search_ref = useRef<HTMLDivElement>(null);

  const resetSelectedRowsRef = useRef<TableRefProps>(null);

  const {
    storefront_id,
    tenant_id,
    storefront_metadata: {
      only_bulk_upload_enabled,
      enable_vertex_ai_search,
      default_portfolio_view,
    },
  } = useStoreState();

  const { t } = useTranslation();
  const { notifyError } = React.useContext(Notifications);

  const { supportedLanguageOptions } = useSupportedLanguages();
  const { hasPermission, roleIsSomeKindOfSeller, roleIsGuest } =
    useAuthContext();

  // Initialize view mode with storefront configuration fallback
  const getInitialViewMode = (): "table" | "grid" => {
    // Cookie preference takes precedence
    if (cookies["products-list-view-mode"]) {
      return cookies["products-list-view-mode"];
    }
    // Map storefront configuration: "classic" -> "table", everything else -> "grid"
    if (default_portfolio_view === "classic") {
      return "table";
    }
    return "grid";
  };
  const [viewMode, setViewMode] = useState<"table" | "grid">(
    getInitialViewMode()
  );

  const currentTotalCount =
    viewMode === "table" ? tableTotalCount : gridTotalCount;

  useEffect(() => {
    if (viewMode === "table" && tableTotalCount > 0) {
      setTablePagination((prev) => ({
        ...prev,
        totalCount: tableTotalCount,
      }));
    } else if (viewMode === "grid" && gridTotalCount > 0) {
      setTablePagination((prev) => ({
        ...prev,
        totalCount: gridTotalCount,
      }));
    }
  }, [viewMode, tableTotalCount, gridTotalCount]);

  const { data: attributesList, error: attributesError } = useSWR<
    WithPagination<{ data: AttributeSchemaBase[] }>,
    Error
  >(
    `${endpoints.v2_storefronts_id_pim_attributes(
      storefront_id
    )}?object_type=product_name&object_type=product_id&object_type=chemical_name&limit=300`
  );

  const { data: customLabels, error: customLabelsError } = useSWR<
    TagClassificationConfig[]
  >(
    storefront_id
      ? endpoints.v2_storefronts_id_products_filters_customLabels(storefront_id)
      : null,
    { revalidateOnFocus: true }
  );

  // Fetch filter metadata for wildcard conversion
  const { data: filterMetadataResponse } = useSWR<ProductFiltersMainSchema>(
    storefront_id
      ? endpoints.v2_storefronts_id_products_filters(storefront_id)
      : null
  );
  const filterMetadata = filterMetadataResponse?.filters || {};

  updateCustomLabels(customLabels, supportedLanguageOptions);

  const {
    data: productsResponse,
    error: productsError,
    mutate,
  } = useAlternateRequest<MinimalApiResponse>({
    filters: {
      ...filtersFromUrl,
      offset: [String(offset)],
      limit: [String(perPage)],
      order_by: [sortingRules.orderBy || "asc"],
      show_inactive: ["true"],
      show_mandatory_completion_score: ["true"],
      show_total_completion_score: ["true"],
      ...(!!debouncedSearchQuery ? { q: [debouncedSearchQuery] } : {}),
      ...(!!sortingRules.sortBy ? { sort_by: [sortingRules.sortBy] } : {}),
    },
    url: endpoints.v2_tenants_id_pim_products_list_minimal(tenant_id),
    filterLabels: customLabels?.map((label) => label.filter_type) || [],
    availableFilters: filterMetadata,
  });

  // Full products data for grid view with infinite scroll

  const {
    data: fullProductsResponse,
    error: fullProductsError,
    isLoading: isLoadingGridProducts,
  } = useAlternateRequest<PIMProductMinimalPaginatedOutput>({
    canFetch: viewMode === "grid",
    filters: {
      ...filtersFromUrl,
      show_inactive: ["true"],
      ...(!!debouncedSearchQuery ? { q: [debouncedSearchQuery] } : {}),
      limit: [String(gridPageLimit)],
      order_by: ["asc"],
    },
    url: endpoints.v2_storefronts_id_pim_products_card(storefront_id),
    filterLabels: customLabels?.map((label) => label.filter_type) || [],
    availableFilters: filterMetadata,
  });

  const { data: presetFilterData, mutate: mutatePresetFilter } = useSWR<
    IPresetFilter,
    Error
  >(
    !roleIsGuest
      ? endpoints.v2_storefronts_id_user_filters(storefront_id)
      : null
  );

  // Function to handle view mode changes and persist to cookie
  const handleViewModeChange = (mode: "table" | "grid") => {
    setViewMode(mode);
    setCookie("products-list-view-mode", mode, {
      path: "/",
      maxAge: 60 * 60 * 24 * 365, // 1 year
    });

    // Clear offset and pagination params when switching views
    setOffset(0);
    setQuery({
      ...query,
      offset: undefined,
      perPage: undefined,
    });

    // Reset infinite scroll when switching to grid view
    if (mode === "grid") {
      setGridPageLimit(10);
    }
  };

  useEffect(() => {
    if (attributesList || attributesError) {
      setTableHeaders({
        product_name:
          attributesList?.data?.find(
            (attribute) => attribute.name === "Product Name"
          )?.display_name || t("Product Name"),
        product_id:
          attributesList?.data?.find(
            (attribute) => attribute.name === "Product ID"
          )?.display_name || t("Product ID"),
        chemical_name:
          attributesList?.data?.find(
            (attribute) => attribute.name === "Chemical Name"
          )?.display_name || t("Chemical Name"),
        last_modified: t("Last Modified"),
        completion: t("Completion"),
        status: t("Status"),
      });
    }
  }, [attributesList, attributesError, t]);

  //table
  const tableColumns = React.useMemo<ColumnDef<SellerTableProduct>[]>(
    () => [
      {
        header: tableHeaders?.product_id,
        id: "externalId",
        accessorKey: "externalId",
        enableSorting: false,
      },
      {
        header: tableHeaders?.product_name,
        accessorKey: "name",
        id: "name",
      },
      {
        header: tableHeaders?.chemical_name,
        accessorKey: "chemical_name",
        id: "chemical_name",
      },
      {
        header: tableHeaders?.last_modified,
        accessorKey: "modified_at",
        id: "modified_at",
        cell: (cell) => (
          <span>{(cell.getValue() as { date: string; id: string }).date}</span>
        ),
      },
      {
        header: tableHeaders?.completion,
        accessorKey: "completion_score",
        cell: ({
          getValue,
        }: {
          getValue: Getter<SellerTableProduct["completion_score"]>;
        }) => {
          const completionScore = getValue();

          // Add defensive check for undefined/null value
          if (!completionScore) {
            return <span>-</span>;
          }

          if (
            !completionScore?.show_total &&
            !completionScore?.show_mandatory
          ) {
            return <span>--</span>;
          }

          const totalScore = completionScore?.show_total
            ? completionScore?.total
            : null;
          const mandatoryScore = completionScore?.show_mandatory
            ? completionScore?.mandatory
            : null;

          // If only total score should be shown
          if (totalScore !== null && mandatoryScore === null) {
            return (
              <>
                <div
                  data-tip={t("Total Completion: {{total}}", {
                    total: totalScore,
                  })}
                  data-for="completion-tooltip"
                >
                  {`${totalScore.toString().padStart(4, " ")}%`}
                </div>
                <ReactTooltip id="completion-tooltip" effect="solid" />
              </>
            );
          }

          // If only mandatory score should be shown
          if (totalScore === null && mandatoryScore !== null) {
            return (
              <>
                <div
                  data-tip={t("Mandatory Completion: {{mandatory}}", {
                    mandatory: mandatoryScore,
                  })}
                  data-for="completion-tooltip"
                >
                  {`${mandatoryScore.toString().padStart(4, " ")}%`}
                </div>
                <ReactTooltip id="completion-tooltip" effect="solid" />
              </>
            );
          }

          // Both scores are shown
          if (totalScore !== null && mandatoryScore !== null) {
            return (
              <>
                <div
                  data-tip={t(
                    "Total Completion: {{total}} | Mandatory Completion: {{mandatory}}",
                    {
                      total: totalScore,
                      mandatory: mandatoryScore,
                    }
                  )}
                  data-for="completion-tooltip"
                >
                  {`${totalScore
                    .toString()
                    .padStart(4, " ")}% | ${mandatoryScore
                    .toString()
                    .padStart(4, " ")}%`}
                </div>
                <ReactTooltip id="completion-tooltip" effect="solid" />
              </>
            );
          }

          // Fallback for unexpected state
          return <span>-</span>;
        },
      },
      {
        header: tableHeaders?.status,
        accessorKey: "status",
        id: "status",
        enableSorting: false,
        align: "left",
        cell: (cell) => (
          <StatusLeft
            color={getProductStatusColor(
              (cell.getValue() as ProductStatusType) ?? "draft"
            )}
            text={getProductStatusText(
              (cell.getValue() as ProductStatusType) ?? "draft",
              t
            )}
            textStyle={{ fontSize: "13px" }}
            productStatusType="product"
          />
        ),
      },
    ],
    [t, tableHeaders]
  );

  const handleSort = async (rules: SortingRule<object>[]) => {
    defaultHandleSort(rules, sortingRules, setSortingRules, setTableData);
  };

  const changePerPage = (perPage: number) => {
    setPerPage(perPage);
    if (perPage > offset) {
      setOffset(0);
    }
  };

  const handleClearFilter = (preventPresetActivation?: boolean) => {
    const destination = pathname;
    history.replace(destination);
    setQuery({ q: query.q, offset: query.offset, perPage: query.per_Page });
    if (viewMode === "grid") {
      // Reset infinite scroll to first page
      setGridPageLimit(10);
    }
    if (selectedPreset && !preventPresetActivation) {
      setSelectedPreset(undefined);
      handleActivePresetId(selectedPreset.id, false).then(() => {
        // Force a refresh of the preset data
        mutatePresetFilter();
      });
    }
    handleSelectedRows([]);
  };

  const handleShowFilters = (showFiltersParam: boolean) => {
    setShowFilters(showFiltersParam);
  };

  const handleActivePresetId = async (
    presetId: string,
    isPresetSelected: boolean
  ) => {
    try {
      const response = await axios.patch(
        endpoints.v2_storefronts_id_user_filters_userFilterId(
          storefront_id,
          presetId
        ),
        {
          selected: isPresetSelected,
        }
      );

      if (response.status === 201) mutatePresetFilter();
    } catch (error) {
      notifyError(t((error as AxiosError)?.response?.data?.message));
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOffset(0);
    setSearchQuery(e.target.value);
    if (e.target.value) {
      localStorage.removeItem(PRODUCT_SEARCH_POPUP);
    }
    if (viewMode === "grid") {
      // Reset infinite scroll to first page on new search
      setGridPageLimit(10);
    }
    handleSelectedRows([]);
  };
  const handleClearSearch = () => {
    setSearchQuery("");
    setQuery({ q: undefined });
    setOffset(0);
    if (viewMode === "grid") {
      // Reset infinite scroll to first page
      setGridPageLimit(10);
    }
    handleSelectedRows([]);
  };

  const changePage = (offset: number) => {
    setOffset(offset);
    setTableData([]);
  };

  const close_search_popup = () => {
    set_show_enterprise_search(false);
    localStorage.setItem(PRODUCT_SEARCH_POPUP, "closed");
  };

  const handleRowClick = (
    e: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => {
    history.push(`${adminPath}/pim/products/${e.currentTarget.id}`);
  };

  useEffect(() => {
    const filters = loadFilters(location);
    setFiltersFromUrl(filters);
  }, [location]);

  useEffect(() => {
    const handleProductsData = ({ data, pagination }: MinimalApiResponse) => {
      setTableData(
        data.map(
          ({
            id,
            name,
            last_modified_full,
            display_status,
            status,
            completion_score,
            mandatory_completion_score,
            display_completion_score,
            display_mandatory_completion_score,
            product_number,
            chemical_name,
            show_total_completion_score,
            show_mandatory_completion_score,
          }) => {
            const totalScore =
              display_status === "staged" ||
              display_status === "unpublished_staged"
                ? display_completion_score
                : completion_score;

            const mandatoryScore =
              display_status === "staged" ||
              display_status === "unpublished_staged"
                ? display_mandatory_completion_score
                : mandatory_completion_score;

            return {
              id: product_number,
              externalId: product_number ?? "--",
              name: name ?? "--",
              chemical_name: chemical_name ?? "--",
              modified_at: {
                date: last_modified_full
                  ? formatDate(last_modified_full.modified_at)
                  : `--`,
                id,
              },
              completion_score: {
                total: totalScore,
                mandatory: mandatoryScore,
                show_total: show_total_completion_score,
                show_mandatory: show_mandatory_completion_score,
              },
              status: display_status ?? status ?? "draft",
              rowURL: `${adminPath}/pim/products/${product_number}`,
            };
          }
        )
      );
      // Update table pagination state
      setTablePagination({
        perPage: perPage,
        pageCount: Math.ceil(pagination.total / perPage),
        pageIndex: pagination.offset / perPage + 1,
        totalCount: pagination.total,
      });
      // Update table total count separately
      setTableTotalCount(pagination.total);
    };

    if (productsResponse) {
      const { data: products, pagination } = productsResponse;
      handleProductsData({ data: products, pagination });
    }
  }, [productsResponse, adminPath, perPage]);

  useEffect(() => {
    setQuery({ offset, perPage });
  }, [offset, perPage, setQuery]);

  useEffect(() => {
    if (query.download) {
      window.open(query.download, "_blank");
      setQuery({ download: undefined });
    }
  }, [query.download, setQuery]);

  // Handle pagination for grid view with infinite scroll
  useEffect(() => {
    if (fullProductsResponse) {
      // Get total from the first page and update grid total count
      const total = fullProductsResponse.pagination?.total || 0;
      setGridTotalCount(total);

      // Also update tablePagination for backward compatibility
      if (viewMode === "grid") {
        setTablePagination((prev) => ({
          ...prev,
          totalCount: total,
        }));
      }
    }
  }, [fullProductsResponse, viewMode]);

  // Intersection observer for infinite scroll (matching PortfolioModernView pattern)
  const { ref: infiniteScrollRef, inView } = useInView({
    triggerOnce: false, // Allow multiple triggers
    rootMargin: "100px", // Start loading when 100px from bottom
  });

  useEffect(() => {
    // This useEffect handles setting the selected product,
    // and keeping the selected product and query in sync with the URL.
    setQuery({ q: !!debouncedSearchQuery ? debouncedSearchQuery : undefined });
    const should_search_popup_be_closed = Boolean(
      localStorage.getItem(PRODUCT_SEARCH_POPUP)
    );
    set_show_enterprise_search(
      !should_search_popup_be_closed &&
        !!debouncedSearchQuery &&
        enable_vertex_ai_search
    );
    if (!debouncedSearchQuery) {
      localStorage.removeItem(PRODUCT_SEARCH_POPUP);
    }
  }, [setQuery, debouncedSearchQuery, enable_vertex_ai_search]);

  useEffect(() => {
    if (filtersFromUrl) {
      const count = Object.entries(filtersFromUrl).reduce(
        (acc, [key, value]) => {
          if (
            key !== "offset" &&
            key !== "perPage" &&
            key !== "q" &&
            key !== "active_tag_view"
          ) {
            return acc + value.length;
          }
          return acc;
        },
        0
      );
      setAppliedFiltersCount(count);
    } else {
      setAppliedFiltersCount(0);
    }
  }, [filtersFromUrl]);

  useEffect(() => {
    if (presetFilterData?.data) {
      const selectedData = presetFilterData.data.find((item) => item.selected);
      setSelectedPreset(selectedData);

      // Automatically apply the selected preset's filters to the URL
      // Only apply if we haven't already applied this preset before
      if (
        selectedData &&
        selectedData.filters &&
        !appliedPresetIds.has(selectedData.id)
      ) {
        // Check if there are existing filters (from either URL or sessionStorage)
        const storageMethod = getStorageMethod(location);
        const hasExistingFilters = storageMethod !== null;

        if (!hasExistingFilters) {
          // Mark this preset as applied
          setAppliedPresetIds((prev) => new Set(prev).add(selectedData.id));

          // Convert preset filters from IFilters to Filters format
          const filters: { [key: string]: string[] } = {};
          Object.entries(selectedData.filters).forEach(([key, values]) => {
            if (Array.isArray(values)) {
              filters[key] = values
                .map((value) => value.name)
                .filter((name) => typeof name === "string");
            }
          });

          // Use saveFilters to handle storage decision automatically
          saveFilters(filters, history, pathname, {
            q: query.q || undefined,
            offset: query.offset ? String(query.offset) : undefined,
            perPage: query.perPage ? String(query.perPage) : undefined,
          });
        }
      }
    }
  }, [
    presetFilterData,
    location,
    pathname,
    history,
    query.q,
    query.offset,
    query.perPage,
    appliedPresetIds,
  ]);

  const isLoading =
    viewMode === "table"
      ? !productsResponse && !productsError
      : !fullProductsResponse && !fullProductsError;

  // Convert to format matching PortfolioModernView
  const products = useMemo(
    () => (fullProductsResponse ? [fullProductsResponse.data] : []),
    [fullProductsResponse]
  );
  const hasProducts = !fullProductsError && products;
  const isLoadingInitialData = !fullProductsResponse && !fullProductsError;
  const isLoadingMore =
    isLoadingInitialData ||
    (isLoadingGridProducts &&
      (fullProductsResponse?.data?.length ?? 0) < gridTotalCount);

  // Load more when scrolling to the bottom
  useEffect(() => {
    if (inView && viewMode === "grid") {
      // Check if there's more data to load
      const totalLoaded = products.flat().length;
      const hasMoreData = totalLoaded < (gridTotalCount || 0);

      if (hasMoreData && !isLoadingMore) {
        setGridPageLimit((size) => size + 10);
      }
    }
  }, [
    inView,
    viewMode,
    products,
    currentTotalCount,
    isLoadingMore,
    gridTotalCount,
  ]);

  useEffect(() => {
    sessionStorage.setItem("products_search", location.search);
  }, [location.search]);

  const placeholder = t("Search by product name or CAS#");

  const search_bar_left_position = search_ref.current?.getBoundingClientRect()
    .left
    ? Math.ceil(search_ref.current?.getBoundingClientRect().left)
    : 0;
  const search_bar_top_position = search_ref.current?.getBoundingClientRect()
    .top
    ? Math.ceil(search_ref.current?.getBoundingClientRect().top)
    : 0;

  return (
    <FullwidthPageWrapper>
      <StickyHeaderContainer>
        <Breadcrumb>
          <div>{t("Products")}</div>
        </Breadcrumb>
        <SearchAndFiltersRow>
          {customLabels && customLabels.length > 0 && (
            <FilterButtonContainer>
              <IconButtonMedium
                onClick={() => handleShowFilters(!showFilters)}
                aria-label={t("Filters")}
              >
                <FiltersIconV2 />
                {!showFilters && appliedFiltersCount > 0 && (
                  <span
                    style={{
                      position: "absolute",
                      top: "-6px",
                      right: "-6px",
                      background: "red",
                      color: "white",
                      borderRadius: "50%",
                      padding: "4px 6px",
                      fontSize: "10px",
                      lineHeight: 1,
                    }}
                  >
                    {appliedFiltersCount}
                  </span>
                )}
              </IconButtonMedium>
            </FilterButtonContainer>
          )}
          <SearchContainerWithMaxWidth>
            <SearchBar
              query={searchQuery}
              placeHolder={placeholder}
              handleChange={handleSearch}
              handleClearInput={handleClearSearch}
              ref={search_ref}
            />
          </SearchContainerWithMaxWidth>
          <RightSideControls>
            <ButtonGroup>
              <ViewSwitcher>
                <ViewButton
                  isActive={viewMode === "grid"}
                  onClick={() => handleViewModeChange("grid")}
                >
                  <ViewGridIcon
                    fill={
                      viewMode === "grid"
                        ? theme.resolve("color_icon_secondary")
                        : theme.resolve("color_icon_disabled")
                    }
                  />
                </ViewButton>
                <ViewButton
                  isActive={viewMode === "table"}
                  onClick={() => handleViewModeChange("table")}
                >
                  <ViewListIcon
                    fill={
                      viewMode === "table"
                        ? theme.resolve("color_icon_secondary")
                        : theme.resolve("color_icon_disabled")
                    }
                  />
                </ViewButton>
              </ViewSwitcher>
              <WithPermission
                permission={["modify_products", "view_templates"]}
                permission_type="every"
              >
                <PrimaryButtonLargeV2
                  onClick={() => history.push(`${adminPath}/pim/products/new`)}
                >
                  <PlusIcon />
                  {only_bulk_upload_enabled ? t("Upload") : t("Create")}
                </PrimaryButtonLargeV2>
              </WithPermission>
            </ButtonGroup>
          </RightSideControls>
        </SearchAndFiltersRow>
      </StickyHeaderContainer>
      <ContentContainer showFilters={showFilters}>
        <StickyFiltersContainer show={showFilters}>
          <FiltersFlyBar
            customLabels={customLabels ?? []}
            handleShowFilters={handleShowFilters}
            show={showFilters}
            selectedPreset={selectedPreset}
            presetFilterData={presetFilterData}
            mutatePresetFilter={mutatePresetFilter}
            handleActivePresetId={handleActivePresetId}
            clearFilter={handleClearFilter}
            resetOffset={setOffset}
          />
        </StickyFiltersContainer>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            overflow: "hidden",
          }}
        >
          <MainContentWrapper
            showFilters={showFilters}
            style={{ display: "flex", flexDirection: "column" }}
          >
            <StickyFiltersAndTotalWrapper>
              {!customLabelsError && (
                <SelectedFiltersWrapper
                  hasFilters={
                    (customLabels && customLabels.length > 0) ?? false
                  }
                >
                  <SelectedFiltersArea
                    clearFilter={handleClearFilter}
                    filtersList={[
                      ...(customLabels?.map(
                        (filter: TagClassificationConfig) => filter.filter_type
                      ) || []),
                      // Add status and template filters for seller_admin
                      ...(roleIsSomeKindOfSeller ? ["status", "template"] : []),
                    ]}
                    isPublic={false}
                  />
                </SelectedFiltersWrapper>
              )}
              {viewMode === "grid" && currentTotalCount > 0 && (
                <TotalProducts>
                  {currentTotalCount} {t("products")}
                </TotalProducts>
              )}
            </StickyFiltersAndTotalWrapper>
            {viewMode === "table" && (
              <TableSearchWrapper style={{ padding: "3px 16px" }}>
                <DropdownContainer>
                  <DropDown
                    items={perPageItems}
                    activeItem={perPage}
                    textLeft={t("items") + ":"}
                    textRight={t(" Per Page")}
                    direction={"right"}
                    className={"per_Page"}
                    clickHandler={changePerPage}
                  ></DropDown>
                  {currentTotalCount > 0 && (
                    <TotalProducts style={{ marginBottom: "0" }}>
                      {currentTotalCount} {t("products")}
                    </TotalProducts>
                  )}
                </DropdownContainer>
              </TableSearchWrapper>
            )}
            {viewMode === "table" ? (
              <>
                {hasPermission("modify_products") ? (
                  <TableWithBulkAction
                    columns={tableColumns}
                    data={tableData}
                    isLoading={isLoading}
                    error={
                      viewMode === "table" ? productsError : fullProductsError
                    }
                    rowHover={rowHover}
                    handleSort={handleSort}
                    handleSelectRows={(rows) => handleSelectedRows(rows)}
                    defaultSelectedRows={selectedRows}
                    bulkActionElement={({ rows, resetSelectedRows }) => (
                      <BulkActionOnProducts
                        rows={
                          rows === "all"
                            ? get_all_condensed_products(
                                tenant_id,
                                new URLSearchParams(
                                  search.substring(1) + "&show_inactive=true"
                                )
                              )
                            : rows
                        }
                        mutateProducts={
                          mutate as unknown as DataMutate<MinimalApiResponse>
                        }
                        onResetSelectedRows={resetSelectedRows}
                      />
                    )}
                    ref={resetSelectedRowsRef}
                    maxHeight="100%"
                    bulkSelectLimit={500}
                  />
                ) : (
                  <Table
                    columns={tableColumns}
                    data={tableData}
                    isLoading={isLoading}
                    error={
                      viewMode === "table" ? productsError : fullProductsError
                    }
                    rowHover={rowHover}
                    handleSort={handleSort}
                    tableHeight="100%"
                  />
                )}
              </>
            ) : (
              <>
                <ProductsGridContainer>
                  {isLoadingInitialData && (
                    <div
                      style={{
                        gridColumn: "1 / -1",
                        padding: "20px",
                        textAlign: "center",
                      }}
                    >
                      <Loader
                        isLoading={isLoadingInitialData}
                        omitContainer={true}
                      />
                    </div>
                  )}
                  {products && products[0]?.length === 0 && (
                    <ErrorPlaceholder message={t("No products found")} />
                  )}
                  {fullProductsError && (
                    <ErrorPlaceholder message={t("Error loading products")} />
                  )}
                  {hasProducts &&
                    products.map((nested) => {
                      return nested.map((listProduct) => {
                        return (
                          <React.Fragment key={listProduct.id}>
                            <CardProduct
                              product={listProduct}
                              href={`${adminPath}/pim/products/${listProduct.product_number}`}
                              onProductClick={(product) => {
                                handleRowClick({
                                  currentTarget: { id: product.product_number },
                                } as React.MouseEvent<HTMLTableRowElement, MouseEvent>);
                              }}
                              onQuoteClick={() => {
                                // Handle seller quote creation
                                setQuoteVariant("seller");
                              }}
                              onSampleClick={() => {
                                // Handle seller sample creation
                                setSampleVariant("seller");
                              }}
                              onDocumentClick={(product, docType) => {
                                // Navigate to product documents page using documentType parameter
                                // Use product_number for admin routes, not slug
                                const productUrl = `${adminPath}/pim/products/${product.product_number}`;

                                // Use the new documentType parameter approach
                                const documentsUrl = `${productUrl}?documentType=${docType}`;
                                history.push(documentsUrl);
                              }}
                            />
                          </React.Fragment>
                        );
                      });
                    })}
                </ProductsGridContainer>
                {/* Intersection observer for infinite scroll - placed outside grid */}
                {hasProducts && (
                  <div
                    ref={infiniteScrollRef}
                    style={{
                      height: "50px",
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {isLoadingMore && !isLoadingInitialData && (
                      <Loader isLoading={true} omitContainer={true} />
                    )}
                  </div>
                )}
              </>
            )}

            {viewMode === "table" && (
              <Pagination
                key={viewMode}
                pagination={tablePagination}
                offset={offset}
                handlePageClick={changePage}
              />
            )}
          </MainContentWrapper>
        </div>
      </ContentContainer>
      {show_enterprise_search && roleIsSomeKindOfSeller && (
        <ProductSearchPopup
          search_bar_left_position={search_bar_left_position}
          search_bar_top_position={search_bar_top_position}
          search_query={searchQuery}
          debounced_search_query={debouncedSearchQuery}
          placeholder={placeholder}
          handle_parent_search={handleSearch}
          handle_parent_clear_search={handleClearSearch}
          search_bar_max_width="450px"
          custom_labels={customLabels}
          close_search_popup={close_search_popup}
          clear_filter={handleClearFilter}
          handle_product_click={(product_id) => {
            handleRowClick({
              currentTarget: { id: product_id },
            } as React.MouseEvent<HTMLTableRowElement, MouseEvent>);
          }}
        />
      )}

      {/* Seller Quote Modal */}
      <Modal
        show={quoteVariant === "seller"}
        closeModal={() => setQuoteVariant(null)}
        modalWidth={"95%"}
        overlay={true}
      >
        {quoteVariant === "seller" && (
          <SellerCreateQuote
            onSuccess={() => {
              setQuoteVariant(null);
            }}
          />
        )}
      </Modal>

      {/* Seller Sample Modal */}
      <Modal
        show={sampleVariant === "seller"}
        closeModal={() => setSampleVariant(null)}
        modalWidth={"95%"}
        overlay={true}
      >
        {sampleVariant === "seller" && (
          <SellerCreateSampleRequest
            onSuccess={(_id: any) => {
              setSampleVariant(null);
            }}
          />
        )}
      </Modal>
    </FullwidthPageWrapper>
  );
}
